import { type FC, useEffect, useState } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import {
  EmploymentStatus,
  type PersonalNetWorthRange,
  useGetEnrollmentPersonalInfoData,
  useSubmitEnrollmentPersonalInfoData,
  type Occupation,
} from '@egw/api'
import { GenericError, Field, RadioGroup, Button, ButtonVariant } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { useForm, Controller } from 'react-hook-form'
import { EnrollmentLayout } from '@egw/layouts'
import { useRouter } from '@egw/router'
import { WEALTH_ROUTES } from '@egw/constants'
import { ROUTES } from '../../../constants'
import { PageHeader } from '../../../components'
import {
  EMPLOYMENT_STATUS,
  employmentStatusItems,
  netWorthRangeItems,
  OCCUPATION,
  occupationItems,
  PERSONAL_NET_WORTH_RANGE,
} from '../../../constants/confirm-personal-info/confirm-personal-info'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'

const log = new Logger('Enrollment - Confirm Personal Info')

export const PersonalInfoPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()

  const [activeBlock, setActiveBlock] = useState<string>('')
  const [isUpdating, setIsUpdating] = useState(false)
  const [isShowOccupation, setIsShowOccupation] = useState(true)

  const { data, isLoading, error } = useGetEnrollmentPersonalInfoData()
  const { mutate: submit, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentPersonalInfoData()

  const {
    control,
    handleSubmit,
    getValues,
    reset,
    formState: { isValid },
  } = useForm({
    defaultValues: {
      [EMPLOYMENT_STATUS]: '',
      [OCCUPATION]: '',
      [PERSONAL_NET_WORTH_RANGE]: '',
    },
  })

  const [previousValues, setPreviousValues] = useState(getValues())

  useEffect(() => {
    if (data?.enrollmentData?.userInfo) {
      reset({
        employmentStatus: data.enrollmentData.userInfo.employmentStatus,
        occupation: data.enrollmentData.userInfo.occupation,
        personalNetWorthRange: data.enrollmentData.userInfo.netWorthRange,
      })

      if (!data.enrollmentData.userInfo.occupation) {
        setIsShowOccupation(false)
      }
    }
  }, [data, navigate, reset])

  useValidateRoute(data?.path)

  const onUpdate = (): void => {
    const isEmployedOrIndependent =
      (getValues(EMPLOYMENT_STATUS) as EmploymentStatus) === EmploymentStatus.EMPLOYED ||
      (getValues(EMPLOYMENT_STATUS) as EmploymentStatus) === EmploymentStatus.INDEPENDENT

    if (isEmployedOrIndependent && activeBlock === EMPLOYMENT_STATUS) {
      setActiveBlock(OCCUPATION)
      setIsShowOccupation(true)
    } else {
      setActiveBlock('')
      setIsShowOccupation(isEmployedOrIndependent)
    }

    setIsUpdating(false)
  }

  const onSubmit = handleSubmit(async (formData: Record<string, unknown>): Promise<void> => {
    const employmentStatus = formData.employmentStatus as EmploymentStatus
    const occupation = formData.occupation as Occupation
    const netWorthRange = formData.personalNetWorthRange as PersonalNetWorthRange

    try {
      const response = await submit({
        employmentStatus,
        ...(isShowOccupation && { occupation }),
        personalNetWorthRange: netWorthRange,
      })

      if (response?.path) {
        navigate(response.path)
      }
    } catch (err) {
      log.error('Error submitting enrollment intro data', err)
    }
  })

  const onEdit = (selectedField: string): void => {
    if (activeBlock !== selectedField && isUpdating) {
      reset(previousValues)
      setIsUpdating(false)
    }

    if (!isUpdating) {
      setPreviousValues(getValues())
    }

    setActiveBlock(selectedField)
  }

  return (
    <EnrollmentLayout title="Personal Info">
      <div className="egw-container-enroll h-full flex flex-col">
        {/* Loading initial data to make sure the user has access to this screen and retrieve data based on user enrollment application */}
        {isLoading ? (
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error && !submitError ? (
          <>
            <PageHeader title={t('investments.confirm-personal-info.title')} />
            <p className="typo-primary-sm-regular text-text-meta">
              {t('investments.confirm-personal-info.description')}
            </p>
            <p className="my-md typo-primary-base-semibold text-text-meta">
              {t('investments.confirm-personal-info.confirm_personal_info')}
            </p>

            <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col h-full">
              {/* Employment Status */}
              {activeBlock !== EMPLOYMENT_STATUS && (
                <Field
                  valueClassName="text-text-meta typo-primary-base-regular"
                  value={t('investments.confirm-personal-info.employment_status_title')}
                  rightValue={t(`investments.confirm-personal-info.employment_status.${getValues(EMPLOYMENT_STATUS)}`)}
                  className="border-b py-md"
                  testId="employment-status"
                  onEdit={() => {
                    onEdit(EMPLOYMENT_STATUS)
                  }}
                />
              )}

              {activeBlock === EMPLOYMENT_STATUS && (
                <div className="bg-background-light px-sm py-sm">
                  <p className="typo-primary-base-semibold mb-xs">
                    {t('investments.confirm-personal-info.your_employment_status')}
                  </p>
                  <Controller
                    name="employmentStatus"
                    control={control}
                    render={({ field: { ref, ...restField } }) => (
                      <RadioGroup
                        {...restField}
                        onChange={(e) => {
                          restField.onChange(e)
                          setIsUpdating(true)
                        }}
                        options={employmentStatusItems.map((item) => ({
                          value: item.value,
                          label: t(`investments.confirm-personal-info.employment_status.${item.value}`),
                        }))}
                        name="employmentStatus"
                        className="pb-sm"
                        testId="employment-status-radio"
                      />
                    )}
                  />
                  <Button
                    onClick={onUpdate}
                    variant={ButtonVariant.Secondary}
                    className="w-full"
                    testId="employment-status-update-btn"
                  >
                    {t('common.update')}
                  </Button>
                </div>
              )}

              {isShowOccupation ? (
                <>
                  {activeBlock !== OCCUPATION && (
                    <Field
                      valueClassName="text-text-meta typo-primary-base-regular"
                      value={t('investments.confirm-personal-info.occupation_title')}
                      rightValue={
                        getValues(OCCUPATION)
                          ? t(`investments.confirm-personal-info.occupation.${getValues(OCCUPATION)}`)
                          : ''
                      }
                      className="border-b py-md"
                      testId="occupation"
                      onEdit={() => {
                        onEdit(OCCUPATION)
                      }}
                    />
                  )}
                </>
              ) : null}

              {activeBlock === OCCUPATION ? (
                <div className="bg-background-light px-sm py-sm">
                  <p className="typo-primary-base-semibold mb-xs">
                    {t('investments.confirm-personal-info.your_occupation')}
                  </p>
                  <Controller
                    name="occupation"
                    control={control}
                    render={({ field: { ref, ...restField } }) => (
                      <RadioGroup
                        {...restField}
                        onChange={(e) => {
                          restField.onChange(e)
                          setIsUpdating(true)
                        }}
                        options={occupationItems.map((item) => ({
                          value: item.value,
                          label: t(`investments.confirm-personal-info.occupation.${item.value}`),
                        }))}
                        name="occupation"
                        className="pb-sm"
                        testId="occupation-radio"
                      />
                    )}
                  />
                  <Button
                    onClick={onUpdate}
                    variant={ButtonVariant.Secondary}
                    className="w-full"
                    testId="occupation-update-btn"
                  >
                    {t('common.update')}
                  </Button>
                </div>
              ) : null}

              {activeBlock !== PERSONAL_NET_WORTH_RANGE && (
                <Field
                  valueClassName="text-text-meta typo-primary-base-regular"
                  value={t('investments.confirm-personal-info.personal_net_worth_title')}
                  rightValue={t(
                    `investments.confirm-personal-info.personal_net_worth_range.${getValues(PERSONAL_NET_WORTH_RANGE)}`,
                  )}
                  className="border-b py-md"
                  testId="personalNetWorthRange"
                  onEdit={() => {
                    onEdit(PERSONAL_NET_WORTH_RANGE)
                  }}
                />
              )}

              {activeBlock === PERSONAL_NET_WORTH_RANGE && (
                <div className="bg-background-light px-sm py-sm">
                  <p className="typo-primary-base-semibold mb-xs">
                    {t('investments.confirm-personal-info.your_personal_net_worth')}
                  </p>
                  <Controller
                    name="personalNetWorthRange"
                    control={control}
                    render={({ field: { ref, ...restField } }) => (
                      <RadioGroup
                        {...restField}
                        onChange={(e) => {
                          restField.onChange(e)
                          setIsUpdating(true)
                        }}
                        options={netWorthRangeItems.map((item) => ({
                          value: item.value,
                          label: t(`investments.confirm-personal-info.personal_net_worth_range.${item.value}`),
                        }))}
                        name="personalNetWorthRange"
                        className="pb-sm"
                        testId="personal-net-worth-radio"
                      />
                    )}
                  />
                  <Button
                    onClick={onUpdate}
                    variant={ButtonVariant.Secondary}
                    className="w-full"
                    testId="personal-net-worth-update-btn"
                  >
                    {t('common.update')}
                  </Button>
                </div>
              )}

              <div className="mt-auto md:mt-lg">
                <Button
                  type="submit"
                  className="w-full md:w-min mt-lg"
                  isDisabled={!isValid || isUpdating}
                  isLoading={isSubmitting}
                  testId="enrollment-continue-button"
                >
                  {t('common.continue')}
                </Button>
              </div>
            </form>
          </>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {!isLoading && !isSubmitting && (error ?? submitError) ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentLayout>
  )
}
